import { supabase } from '../supabase/client';
import type { Json } from 'src/types/db.types';
import { transactionService } from '../transaction.service';
import { studentService } from '../student.service';
import { buildPayPeriodKey, formatMoney, roundMoney } from './calculatePay';
import { matchPayrollRows, summarizeMatchedRows, type StudentMatchCandidate } from './matchStudents';
import {
  parsePayrollCsv,
  parseRosterCsv,
  applyAnomalyResolution,
  type PayrollMatchStatus,
} from './parsePayrollCsv';
import { PAYROLL_CONFIG } from './payroll.config';
import type {
  ApprovePayrollResult,
  CreatePayrollPreviewInput,
  PayrollImport,
  PayrollImportListResult,
  PayrollImportRow,
  PayrollImportWithRows,
  PayrollSnapshot,
  PayrollTotals,
  RosterCreateResult,
  RosterPreviewRow,
  TaxTransactionDetail,
  TaxYtdRow,
} from 'src/types/payroll.types';

const IMPORTS_TABLE = 'payroll_imports';
const ROWS_TABLE = 'payroll_import_rows';

function periodsToJson(periods: Record<string | number, number>): Record<string, number> {
  const out: Record<string, number> = {};
  Object.entries(periods || {}).forEach(([k, v]) => {
    out[String(k)] = v;
  });
  return out;
}

function mapImport(row: any): PayrollImport {
  const totals = (row.totals as PayrollTotals) || ({} as PayrollTotals);
  const gross = Number(totals.total_gross || 0);
  const tax = Number(totals.total_tax || 0);
  const net = Number(totals.total_net || 0);
  const name = row.file_name || row.pay_period_key || `Import #${row.id.substring(0, 8)}`;

  return {
    id: row.id,
    uploaded_by: row.uploaded_by,
    pay_period_start: row.pay_period_start,
    pay_period_end: row.pay_period_end,
    pay_period_key: row.pay_period_key,
    tax_rate: Number(row.tax_rate),
    status: row.status,
    file_name: row.file_name,
    name,
    note: row.note,
    totals,
    totalgrosspay: gross,
    totaltaxwithheld: tax,
    totalnetpay: net,
    createdat: row.createdat,
    approved_at: row.approved_at,
    updatedat: row.updatedat,
  };
}

function mapRow(row: any): PayrollImportRow {
  const periodsData = row.periods || {};
  const courses = periodsData._courses || row.courses || [];
  const anomalies = periodsData._anomalies || row.anomalies || [];
  const cleanPeriods: Record<string, number> = {};
  Object.keys(periodsData).forEach((k) => {
    if (!k.startsWith('_')) {
      cleanPeriods[k] = Number(periodsData[k]) || 0;
    }
  });

  return {
    id: row.id,
    payroll_import_id: row.payroll_import_id,
    row_number: row.row_number,
    external_student_id: row.external_student_id,
    first_name: row.first_name,
    last_name: row.last_name,
    grade_level: row.grade_level,
    date_pulled: row.date_pulled,
    periods: cleanPeriods,
    courses: courses.length > 0 ? courses : undefined,
    anomalies: anomalies.length > 0 ? anomalies : undefined,
    gross: Number(row.gross),
    tax_rate: Number(row.tax_rate),
    tax_withheld: Number(row.tax_withheld),
    net: Number(row.net),
    match_status: row.match_status as PayrollMatchStatus,
    student_id: row.student_id,
    transaction_id: row.transaction_id,
    pay_period_key: row.pay_period_key,
    error_message: row.error_message,
    warnings: Array.isArray(row.warnings) ? row.warnings : [],
    createdat: row.createdat,
    updatedat: row.updatedat,
  };
}

async function loadStudentsForMatching(): Promise<StudentMatchCandidate[]> {
  const { data, error } = await supabase
    .from('students')
    .select(
      `
      id,
      external_student_id,
      grade,
      user:users!inner(
        firstname,
        lastname,
        email,
        deletedat,
        extendedproperties
      )
    `
    );

  if (error) throw error;

  return (data || []).map((s: any) => {
    const legacyId = s.user?.extendedproperties?.studentId;
    const external =
      s.external_student_id ||
      (legacyId != null && String(legacyId).trim() !== '' ? String(legacyId).trim() : null);

    return {
      id: s.id,
      externalStudentId: external,
      firstName: s.user?.firstname || '',
      lastName: s.user?.lastname || '',
      email: s.user?.email || null,
      grade: s.grade,
      userDeletedAt: s.user?.deletedat || null,
    };
  });
}

async function loadAlreadyPaidKeys(payPeriodKey: string): Promise<Set<string>> {
  const { data, error } = await supabase
    .from(ROWS_TABLE)
    .select('student_id, pay_period_key')
    .eq('pay_period_key', payPeriodKey)
    .eq('match_status', 'posted')
    .not('student_id', 'is', null);

  if (error) throw error;

  const keys = new Set<string>();
  (data || []).forEach((r: any) => {
    if (r.student_id) keys.add(`${r.student_id}::${r.pay_period_key}`);
  });
  return keys;
}

async function getPaycheckTransactionTypeId(): Promise<string> {
  const { data, error } = await supabase
    .from('transaction_types')
    .select('id')
    .eq('name', PAYROLL_CONFIG.paycheckTransactionTypeName)
    .is('deletedat', null)
    .maybeSingle();

  if (error) throw error;
  if (data?.id) return data.id;

  // Fallback create if seed wasn't applied yet
  const { data: created, error: createError } = await supabase
    .from('transaction_types')
    .insert({
      name: PAYROLL_CONFIG.paycheckTransactionTypeName,
      description: 'Biweekly grade-based payroll (net deposit after tax withholding)',
      type: 'credit',
      points: 0,
      extendedproperties: {
        category: 'payroll',
        source: 'payroll_import',
        requiresNotification: true,
      },
    })
    .select('id')
    .single();

  if (createError) throw createError;
  return created.id;
}

function buildSnapshot(importId: string, payPeriodKey: string, row: PayrollImportRow | any): PayrollSnapshot {
  return {
    source: 'payroll_import',
    payroll_import_id: importId,
    pay_period_key: payPeriodKey,
    date_pulled: row.date_pulled,
    periods: row.periods,
    courses: row.courses,
    anomalies: row.anomalies,
    gross: Number(row.gross),
    tax_rate: Number(row.tax_rate),
    tax_withheld: Number(row.tax_withheld),
    net: Number(row.net),
    first_name: row.first_name,
    last_name: row.last_name,
    external_student_id: row.external_student_id,
  };
}

export const payrollImportService = {
  /**
   * Parse CSV in browser, match students, persist preview batch + rows.
   */
  async createPreview(input: CreatePayrollPreviewInput): Promise<PayrollImportWithRows> {
    const taxRate = input.taxRate ?? PAYROLL_CONFIG.taxRate;
    const payPeriodKey = buildPayPeriodKey(input.payPeriodStart, input.payPeriodEnd);

    const parsed = parsePayrollCsv(input.csvText, input.fileName, taxRate);
    if (parsed.headerIssues.length > 0 && parsed.rows.length === 0) {
      throw new Error(parsed.headerIssues.join('. '));
    }
    if (parsed.headerIssues.length > 0) {
      // Soft warning — still allow if we have usable rows
      console.warn('[PayrollImport] header issues:', parsed.headerIssues);
    }
    if (parsed.rows.length === 0) {
      throw new Error('No data rows found in CSV');
    }

    const students = await loadStudentsForMatching();
    const alreadyPaidKeys = await loadAlreadyPaidKeys(payPeriodKey);
    const matched = matchPayrollRows(parsed.rows, students, payPeriodKey, { keys: alreadyPaidKeys });
    const summary = summarizeMatchedRows(matched);

    const totals: PayrollTotals = {
      matched_count: summary.matchedCount,
      error_count: summary.errorCount,
      warning_count: summary.warningCount,
      total_rows: summary.totalRows,
      total_gross: summary.totalGross,
      total_tax: summary.totalTax,
      total_net: summary.totalNet,
    };

    const { data: importRow, error: importError } = await supabase
      .from(IMPORTS_TABLE)
      .insert({
        uploaded_by: input.uploadedBy || null,
        pay_period_start: input.payPeriodStart,
        pay_period_end: input.payPeriodEnd,
        pay_period_key: payPeriodKey,
        tax_rate: taxRate,
        status: 'preview',
        file_name: input.fileName,
        note: input.note || null,
        totals: totals as unknown as Json,
      })
      .select('*')
      .single();

    if (importError) throw importError;

    const rowInserts = matched.map((row) => ({
      payroll_import_id: importRow.id,
      row_number: row.rowNumber,
      external_student_id: row.externalStudentId || null,
      first_name: row.firstName || null,
      last_name: row.lastName || null,
      grade_level: row.gradeLevel || null,
      date_pulled: row.datePulled || null,
      periods: {
        ...periodsToJson(row.periods),
        ...(row.courses ? { _courses: row.courses } : {}),
        ...(row.anomalies ? { _anomalies: row.anomalies } : {}),
      },
      gross: row.gross,
      tax_rate: row.taxRate,
      tax_withheld: row.taxWithheld,
      net: row.net,
      match_status: row.matchStatus,
      student_id: row.studentId,
      pay_period_key: payPeriodKey,
      error_message: row.errorMessage,
      warnings: row.warnings,
    }));

    const { data: insertedRows, error: rowsError } = await supabase
      .from(ROWS_TABLE)
      .insert(rowInserts)
      .select('*');

    if (rowsError) {
      // Rollback import shell
      await supabase.from(IMPORTS_TABLE).delete().eq('id', importRow.id);
      throw rowsError;
    }

    return {
      ...mapImport(importRow),
      rows: (insertedRows || []).map(mapRow),
    };
  },

  async getImport(importId: string): Promise<PayrollImportWithRows> {
    const { data: imp, error } = await supabase.from(IMPORTS_TABLE).select('*').eq('id', importId).single();
    if (error) throw error;

    const { data: rows, error: rowsError } = await supabase
      .from(ROWS_TABLE)
      .select('*')
      .eq('payroll_import_id', importId)
      .order('row_number', { ascending: true });

    if (rowsError) throw rowsError;

    return {
      ...mapImport(imp),
      rows: (rows || []).map(mapRow),
    };
  },

  async listImports(page = 1, limit = 20): Promise<PayrollImportListResult> {
    const from = (page - 1) * limit;
    const to = from + limit - 1;

    const { data, error, count } = await supabase
      .from(IMPORTS_TABLE)
      .select('*', { count: 'exact' })
      .order('createdat', { ascending: false })
      .range(from, to);

    if (error) throw error;

    const total = count || 0;
    const mapped = (data || []).map(mapImport);

    return {
      data: mapped,
      items: mapped,
      total,
      totalPages: Math.ceil(total / limit) || 1,
    };
  },

  /**
   * Approve a preview batch: create net credit transactions for payable rows.
   * Succeeded rows are never re-paid; failures can be retried.
   */
  async approveImport(
    importId: string,
    options?: {
      onProgress?: (done: number, total: number, current?: string) => void;
      retryFailedOnly?: boolean;
    }
  ): Promise<ApprovePayrollResult> {
    const batch = await this.getImport(importId);

    if (batch.status === 'approved') {
      throw new Error('This payroll import is already fully approved');
    }
    if (batch.status === 'cancelled') {
      throw new Error('This payroll import was cancelled');
    }

    // Re-check already paid for safety
    const alreadyPaidKeys = await loadAlreadyPaidKeys(batch.pay_period_key);
    const typeId = await getPaycheckTransactionTypeId();

    const payableStatuses: PayrollMatchStatus[] = ['matched', 'name_mismatch_warning', 'post_failed'];
    let candidates = batch.rows.filter((r) => {
      if (options?.retryFailedOnly) return r.match_status === 'post_failed';
      return payableStatuses.includes(r.match_status) && !r.transaction_id;
    });

    // Filter already paid at approve time
    candidates = candidates.filter((r) => {
      if (!r.student_id) return false;
      const key = `${r.student_id}::${batch.pay_period_key}`;
      if (alreadyPaidKeys.has(key) && r.match_status !== 'post_failed') {
        return false;
      }
      // post_failed rows that somehow got paid elsewhere — skip
      if (alreadyPaidKeys.has(key) && r.transaction_id) return false;
      if (alreadyPaidKeys.has(key)) return false;
      return true;
    });

    // Mark newly discovered already_paid rows
    const newlyBlocked = batch.rows.filter((r) => {
      if (!r.student_id || r.transaction_id) return false;
      if (!['matched', 'name_mismatch_warning'].includes(r.match_status)) return false;
      return alreadyPaidKeys.has(`${r.student_id}::${batch.pay_period_key}`);
    });

    await Promise.all(
      newlyBlocked.map((r) =>
        supabase
          .from(ROWS_TABLE)
          .update({
            match_status: 'already_paid',
            error_message: `Already paid for period ${batch.pay_period_key}`,
            updatedat: new Date().toISOString(),
          })
          .eq('id', r.id)
      )
    );

    let posted = 0;
    let failed = 0;
    const failures: ApprovePayrollResult['failures'] = [];

    for (let i = 0; i < candidates.length; i += 1) {
      const row = candidates[i];
      options?.onProgress?.(i, candidates.length, `${row.first_name} ${row.last_name}`);

      try {
        if (!row.student_id) throw new Error('Missing student match');

        // Final race guard
        const paidNow = await loadAlreadyPaidKeys(batch.pay_period_key);
        if (paidNow.has(`${row.student_id}::${batch.pay_period_key}`)) {
          await supabase
            .from(ROWS_TABLE)
            .update({
              match_status: 'already_paid',
              error_message: `Already paid for period ${batch.pay_period_key}`,
              updatedat: new Date().toISOString(),
            })
            .eq('id', row.id);
          continue;
        }

        const snapshot = buildSnapshot(importId, batch.pay_period_key, row);
        const description = `Paycheck ${batch.pay_period_key} · Gross ${formatMoney(row.gross)} · Tax ${formatMoney(
          row.tax_withheld
        )} · Net ${formatMoney(row.net)}`;

        const { id: transactionId } = await transactionService.createTransaction({
          studentId: row.student_id,
          transactionTypeId: typeId,
          amount: row.net,
          description,
          reason: 'Scholar Dollar payroll',
          extendedProperties: {
            payroll: snapshot,
          },
        });

        const { error: updateError } = await supabase
          .from(ROWS_TABLE)
          .update({
            match_status: 'posted',
            transaction_id: transactionId,
            error_message: null,
            updatedat: new Date().toISOString(),
          })
          .eq('id', row.id);

        if (updateError) throw updateError;

        posted += 1;
        alreadyPaidKeys.add(`${row.student_id}::${batch.pay_period_key}`);
      } catch (err: any) {
        failed += 1;
        const message = err?.message || 'Failed to post paycheck';
        failures.push({
          rowId: row.id,
          externalStudentId: row.external_student_id,
          error: message,
        });
        await supabase
          .from(ROWS_TABLE)
          .update({
            match_status: 'post_failed',
            error_message: message,
            updatedat: new Date().toISOString(),
          })
          .eq('id', row.id);
      }
    }

    options?.onProgress?.(candidates.length, candidates.length);

    // Refresh totals / status
    const refreshed = await this.getImport(importId);
    const postedCount = refreshed.rows.filter((r) => r.match_status === 'posted').length;
    const failedCount = refreshed.rows.filter((r) => r.match_status === 'post_failed').length;
    const payableLeft = refreshed.rows.filter((r) =>
      ['matched', 'name_mismatch_warning', 'post_failed'].includes(r.match_status)
    ).length;

    let status: ApprovePayrollResult['status'] = 'approved';
    if (postedCount === 0 && failedCount > 0) status = 'partial';
    else if (failedCount > 0 || payableLeft > 0) status = 'partial';
    else if (postedCount > 0) status = 'approved';
    else status = 'preview';

    // If nothing was payable at all and nothing posted, keep preview
    if (postedCount === 0 && failedCount === 0) {
      status = batch.status === 'preview' ? 'preview' : batch.status;
    }

    const newTotals: PayrollTotals = {
      ...refreshed.totals,
      posted_count: postedCount,
      failed_count: failedCount,
    };

    await supabase
      .from(IMPORTS_TABLE)
      .update({
        status,
        totals: newTotals as unknown as Json,
        approved_at: status === 'approved' || status === 'partial' ? new Date().toISOString() : refreshed.approved_at,
        updatedat: new Date().toISOString(),
      })
      .eq('id', importId);

    return {
      importId,
      status,
      posted,
      failed,
      failures,
    };
  },

  async cancelImport(importId: string): Promise<void> {
    const batch = await this.getImport(importId);
    if (batch.status === 'approved') {
      throw new Error('Cannot cancel an approved payroll import');
    }
    if (batch.rows.some((r) => r.match_status === 'posted')) {
      throw new Error('Cannot cancel — some paychecks were already posted. Retry failures instead.');
    }
    const { error } = await supabase
      .from(IMPORTS_TABLE)
      .update({ status: 'cancelled', updatedat: new Date().toISOString() })
      .eq('id', importId);
    if (error) throw error;
  },

  /**
   * Delete an import that has not posted any paychecks.
   * Posted imports are retained as financial history and cannot be removed here.
   */
  async deleteImport(importId: string): Promise<void> {
    const batch = await this.getImport(importId);
    if (batch.rows.some((r) => r.match_status === 'posted' || r.transaction_id)) {
      throw new Error('This payroll import contains posted paychecks and cannot be deleted.');
    }

    const { error } = await supabase.from(IMPORTS_TABLE).delete().eq('id', importId);
    if (error) throw error;
  },

  /**
   * Update an anomaly resolution for a specific row in a preview import.
   * Recomputes the row's periods, gross, tax, net, and recalculates batch totals.
   */
  async updateRowAnomalyResolution(
    importId: string,
    rowId: string,
    periodName: string,
    resolution: 'keep_both' | 'keep_first' | 'keep_second' | 'average'
  ): Promise<PayrollImportWithRows> {
    const { data: rowData, error: rowError } = await supabase
      .from(ROWS_TABLE)
      .select('*')
      .eq('id', rowId)
      .eq('payroll_import_id', importId)
      .single();

    if (rowError || !rowData) {
      throw new Error('Row not found for resolution update');
    }

    const mapped = mapRow(rowData);
    const resolved = applyAnomalyResolution(
      mapped.periods,
      mapped.anomalies,
      periodName,
      resolution,
      mapped.tax_rate
    );

    const updatedPeriodsJson = {
      ...periodsToJson(resolved.periods),
      ...(mapped.courses ? { _courses: mapped.courses } : {}),
      _anomalies: resolved.anomalies,
    };

    const { error: updateError } = await supabase
      .from(ROWS_TABLE)
      .update({
        periods: updatedPeriodsJson,
        gross: resolved.gross,
        tax_withheld: resolved.taxWithheld,
        net: resolved.net,
      })
      .eq('id', rowId);

    if (updateError) throw updateError;

    // Recalculate import totals
    const { data: allRows, error: allRowsError } = await supabase
      .from(ROWS_TABLE)
      .select('*')
      .eq('payroll_import_id', importId);

    if (allRowsError) throw allRowsError;

    const mappedAll = (allRows || []).map(mapRow);
    const payable = mappedAll.filter(
      (r) => r.match_status === 'matched' || r.match_status === 'name_mismatch_warning'
    );
    const totalGross = roundMoney(payable.reduce((s, r) => s + r.gross, 0));
    const totalTax = roundMoney(payable.reduce((s, r) => s + r.tax_withheld, 0));
    const totalNet = roundMoney(payable.reduce((s, r) => s + r.net, 0));

    const { data: importData } = await supabase
      .from(IMPORTS_TABLE)
      .select('totals')
      .eq('id', importId)
      .single();

    const currentTotals = (importData?.totals as any) || {};
    const updatedTotals = {
      ...currentTotals,
      total_gross: totalGross,
      total_tax: totalTax,
      total_net: totalNet,
    };

    await supabase
      .from(IMPORTS_TABLE)
      .update({ totals: updatedTotals })
      .eq('id', importId);

    return this.getImport(importId);
  },

  /**
   * YTD tax export for Tax Day prep — sums stored snapshots from posted rows.
   */
  async getTaxYtd(year?: number): Promise<TaxYtdRow[]> {
    const y = year || new Date().getFullYear();
    const yearPrefix = `${y}-`;

    const { data, error } = await supabase
      .from(ROWS_TABLE)
      .select(
        `
        id,
        payroll_import_id,
        student_id,
        external_student_id,
        first_name,
        last_name,
        grade_level,
        gross,
        tax_rate,
        tax_withheld,
        net,
        periods,
        pay_period_key,
        match_status,
        transaction_id,
        date_pulled,
        createdat,
        updatedat,
        student:students(
          id,
          external_student_id,
          user:users(firstname, lastname)
        )
      `
      )
      .eq('match_status', 'posted')
      .like('pay_period_key', `${yearPrefix}%`);

    if (error) throw error;

    const byStudent = new Map<string, TaxYtdRow>();

    (data || []).forEach((row: any) => {
      const studentId = row.student_id;
      if (!studentId) return;

      const externalStudentId =
        row.student?.external_student_id || row.external_student_id || null;
      const firstName = row.student?.user?.firstname || row.first_name || '';
      const lastName = row.student?.user?.lastname || row.last_name || '';

      const periodsData = row.periods || {};
      const courses = periodsData._courses || row.courses || [];
      const cleanPeriods: Record<string, number> = {};
      Object.keys(periodsData).forEach((k) => {
        if (!k.startsWith('_')) {
          cleanPeriods[k] = Number(periodsData[k]) || 0;
        }
      });

      const tx: TaxTransactionDetail = {
        id: row.id,
        transactionId: row.transaction_id || null,
        payrollImportId: row.payroll_import_id,
        studentId,
        externalStudentId,
        firstName,
        lastName,
        gradeLevel: row.grade_level || null,
        payPeriodKey: row.pay_period_key || '',
        dateRecorded: row.updatedat || row.createdat,
        datePulled: row.date_pulled || null,
        gross: Number(row.gross) || 0,
        taxRate: Number(row.tax_rate) || 0.1,
        taxWithheld: Number(row.tax_withheld) || 0,
        net: Number(row.net) || 0,
        periods: cleanPeriods,
        courses: courses.length > 0 ? courses : undefined,
      };

      const existing = byStudent.get(studentId) || {
        studentId,
        externalStudentId,
        firstName,
        lastName,
        gradeLevel: row.grade_level || null,
        ytdGross: 0,
        ytdTaxWithheld: 0,
        ytdNet: 0,
        effectiveTaxRate: 10,
        paycheckCount: 0,
        transactions: [] as TaxTransactionDetail[],
      };

      existing.ytdGross += Number(row.gross) || 0;
      existing.ytdTaxWithheld += Number(row.tax_withheld) || 0;
      existing.ytdNet += Number(row.net) || 0;
      existing.paycheckCount += 1;
      existing.transactions.push(tx);
      byStudent.set(studentId, existing);
    });

    return Array.from(byStudent.values())
      .map((r) => {
        const ytdGross = Math.round(r.ytdGross * 100) / 100;
        const ytdTaxWithheld = Math.round(r.ytdTaxWithheld * 100) / 100;
        const ytdNet = Math.round(r.ytdNet * 100) / 100;
        const effectiveTaxRate =
          ytdGross > 0 ? Math.round((ytdTaxWithheld / ytdGross) * 1000) / 10 : 10;
        const sortedTx = [...r.transactions].sort(
          (a, b) => new Date(b.dateRecorded).getTime() - new Date(a.dateRecorded).getTime()
        );

        return {
          ...r,
          ytdGross,
          ytdTaxWithheld,
          ytdNet,
          effectiveTaxRate,
          transactions: sortedTx,
        };
      })
      .sort((a, b) => a.lastName.localeCompare(b.lastName) || a.firstName.localeCompare(b.firstName));
  },

  exportTaxYtdCsv(rows: TaxYtdRow[]): string {
    const header = 'student_id,first_name,last_name,ytd_gross,ytd_tax_withheld,ytd_net,paycheck_count';
    const lines = rows.map(
      (r) =>
        `${csvEscape(r.externalStudentId || '')},${csvEscape(r.firstName)},${csvEscape(r.lastName)},${r.ytdGross.toFixed(
          2
        )},${r.ytdTaxWithheld.toFixed(2)},${r.ytdNet.toFixed(2)},${r.paycheckCount}`
    );
    return [header, ...lines].join('\n');
  },

  exportTaxTransactionsCsv(transactions: TaxTransactionDetail[]): string {
    const header =
      'date_recorded,transaction_id,student_id,first_name,last_name,grade_level,pay_period,gross,tax_rate,tax_withheld,net';
    const lines = transactions.map(
      (t) =>
        `${csvEscape(t.dateRecorded)},${csvEscape(t.transactionId || '')},${csvEscape(
          t.externalStudentId || ''
        )},${csvEscape(t.firstName)},${csvEscape(t.lastName)},${csvEscape(t.gradeLevel || '')},${csvEscape(
          t.payPeriodKey
        )},${t.gross.toFixed(2)},${(t.taxRate * 100).toFixed(1)}%,${t.taxWithheld.toFixed(2)},${t.net.toFixed(2)}`
    );
    return [header, ...lines].join('\n');
  },

  exportProblemRowsCsv(rows: PayrollImportRow[]): string {
    const header =
      'row_number,student_id,first_name,last_name,match_status,error_message,gross,tax,net';
    const lines = rows.map(
      (r) =>
        `${r.row_number},${csvEscape(r.external_student_id || '')},${csvEscape(r.first_name || '')},${csvEscape(
          r.last_name || ''
        )},${r.match_status},${csvEscape(r.error_message || '')},${Number(r.gross).toFixed(2)},${Number(
          r.tax_withheld
        ).toFixed(2)},${Number(r.net).toFixed(2)}`
    );
    return [header, ...lines].join('\n');
  },

  exportCredentialsCsv(credentials: RosterCreateResult['credentials']): string {
    const header = 'student_id,first_name,last_name,email,action,password';
    const lines = credentials.map(
      (c) =>
        `${csvEscape(c.externalStudentId)},${csvEscape(c.firstName)},${csvEscape(c.lastName)},${csvEscape(
          c.email
        )},${csvEscape(c.action || 'created')},${csvEscape(
          c.action === 'updated'
            ? 'N/A (password unchanged)'
            : c.password || c.tempPassword || c.externalStudentId
        )}`
    );
    return [header, ...lines].join('\n');
  },

  /**
   * Preview roster CSV — does not create accounts yet.
   */
  async previewRoster(csvText: string): Promise<RosterPreviewRow[]> {
    const parsed = parseRosterCsv(csvText);
    if (parsed.headerIssues.length && parsed.rows.length === 0) {
      throw new Error(parsed.headerIssues.join('. '));
    }

    const students = await loadStudentsForMatching();
    const studentByExternalId = new Map<string, StudentMatchCandidate>();
    students.forEach((s) => {
      if (s.externalStudentId) {
        studentByExternalId.set(s.externalStudentId.trim().toLowerCase(), s);
      }
    });

    const seenInFile = new Map<string, number>();

    return parsed.rows.map((row) => {
      const key = row.externalStudentId.trim().toLowerCase();
      if (row.parseErrors.length) {
        return {
          rowNumber: row.rowNumber,
          externalStudentId: row.externalStudentId,
          firstName: row.firstName,
          lastName: row.lastName,
          gradeLevel: row.gradeLevel,
          status: 'error' as const,
          message: row.parseErrors.join('; '),
        };
      }

      const count = (seenInFile.get(key) || 0) + 1;
      seenInFile.set(key, count);
      if (count > 1) {
        return {
          rowNumber: row.rowNumber,
          externalStudentId: row.externalStudentId,
          firstName: row.firstName,
          lastName: row.lastName,
          gradeLevel: row.gradeLevel,
          status: 'error' as const,
          message: 'Duplicate Student ID in file',
        };
      }

      const existingStudent = studentByExternalId.get(key);
      const email = (row.email && row.email.trim()) || buildRosterEmail(row.externalStudentId, row.firstName, row.lastName);

      if (existingStudent) {
        const isCurrentlyInactive = Boolean(existingStudent.userDeletedAt || existingStudent.deletedAt);
        const existingEmail = (existingStudent.email || '').trim().toLowerCase();
        const nextEmail = email.trim().toLowerCase();
        const gradeMatch = row.gradeLevel ? row.gradeLevel.match(/\d+/) : null;
        const nextGrade = gradeMatch ? Number(gradeMatch[0]) : null;
        const hasEmailChange = Boolean(nextEmail && nextEmail !== existingEmail);
        const hasGradeChange = Boolean(
          nextGrade != null && existingStudent.grade != null && nextGrade !== existingStudent.grade
        );

        if (isCurrentlyInactive || hasEmailChange || hasGradeChange) {
          const changeNotes: string[] = [];
          if (isCurrentlyInactive) changeNotes.push('Reactivate archived account');
          if (hasEmailChange) changeNotes.push(`Email: ${existingStudent.email || 'None'} → ${email}`);
          if (hasGradeChange) changeNotes.push(`Grade: ${existingStudent.grade} → ${nextGrade}`);

          return {
            rowNumber: row.rowNumber,
            externalStudentId: row.externalStudentId,
            firstName: row.firstName,
            lastName: row.lastName,
            gradeLevel: row.gradeLevel,
            status: 'update' as const,
            studentId: existingStudent.id,
            email,
            message: `Update ${changeNotes.join(', ')}`,
          };
        }

        return {
          rowNumber: row.rowNumber,
          externalStudentId: row.externalStudentId,
          firstName: row.firstName,
          lastName: row.lastName,
          gradeLevel: row.gradeLevel,
          status: 'skip_exists' as const,
          message: 'Student already exists (up to date)',
          email: existingStudent.email || email,
        };
      }

      return {
        rowNumber: row.rowNumber,
        externalStudentId: row.externalStudentId,
        firstName: row.firstName,
        lastName: row.lastName,
        gradeLevel: row.gradeLevel,
        status: 'create' as const,
        email,
      };
    });
  },

  /**
   * Create or update student accounts from roster preview rows marked create or update.
   * Returns credentials CSV payload once.
   */
  async createRosterStudents(rows: RosterPreviewRow[]): Promise<RosterCreateResult> {
    const toProcess = rows.filter((r) => r.status === 'create' || r.status === 'update');
    const result: RosterCreateResult = {
      created: 0,
      updated: 0,
      skipped: rows.filter((r) => r.status === 'skip_exists').length,
      failed: 0,
      credentials: [],
      failures: [],
    };

    for (const row of toProcess) {
      try {
        const email = (row.email && row.email.trim()) || buildRosterEmail(row.externalStudentId, row.firstName, row.lastName);
        const gradeMatch = row.gradeLevel ? row.gradeLevel.match(/\d+/) : null;
        const grade = gradeMatch ? gradeMatch[0] : '9';

        if (row.status === 'update' && row.studentId) {
          await studentService.updateStudent(row.studentId, {
            firstname: row.firstName,
            lastname: row.lastName,
            email,
            studentId: row.externalStudentId,
            grade,
          });

          result.updated = (result.updated || 0) + 1;
          result.credentials.push({
            externalStudentId: row.externalStudentId,
            firstName: row.firstName,
            lastName: row.lastName,
            email,
            action: 'updated',
          });
        } else {
          const studentPassword = row.externalStudentId.trim();
          await studentService.createStudent({
            firstname: row.firstName,
            lastname: row.lastName,
            email,
            phonenumber: '',
            studentId: row.externalStudentId,
            grade,
            password: studentPassword,
          });

          result.created += 1;
          result.credentials.push({
            externalStudentId: row.externalStudentId,
            firstName: row.firstName,
            lastName: row.lastName,
            email,
            tempPassword: studentPassword,
            password: studentPassword,
            action: 'created',
          });
        }
      } catch (err: any) {
        result.failed += 1;
        result.failures.push({
          externalStudentId: row.externalStudentId,
          error: err?.message || `${row.status === 'update' ? 'Update' : 'Create'} failed`,
        });
      }
    }

    return result;
  },

  /**
   * Fetch paycheck stub data for a transaction (admin or student view).
   */
  async getPaycheckStub(transactionId: string): Promise<PayrollSnapshot | null> {
    const { data, error } = await supabase
      .from('transactions')
      .select('id, amount, description, extendedproperties, createdat')
      .eq('id', transactionId)
      .single();

    if (error) throw error;
    const payroll = (data?.extendedproperties as any)?.payroll;
    if (!payroll || payroll.source !== 'payroll_import') return null;
    return payroll as PayrollSnapshot;
  },

  /**
   * List paycheck transactions for a student (for stub list).
   */
  async listStudentPaychecks(studentId: string) {
    const { data: type } = await supabase
      .from('transaction_types')
      .select('id')
      .eq('name', PAYROLL_CONFIG.paycheckTransactionTypeName)
      .is('deletedat', null)
      .maybeSingle();

    if (!type?.id) return [];

    const { data, error } = await supabase
      .from('transactions')
      .select('id, amount, description, extendedproperties, createdat, status')
      .eq('studentid', studentId)
      .eq('transactiontypeid', type.id)
      .order('createdat', { ascending: false });

    if (error) throw error;
    return data || [];
  },
};

function csvEscape(value: string): string {
  if (value.includes(',') || value.includes('"') || value.includes('\n')) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
}

function buildRosterEmail(externalStudentId: string, firstName?: string, lastName?: string): string {
  if (firstName && lastName) {
    const f = firstName
      .trim()
      .toLowerCase()
      .replace(/['’]/g, '')
      .replace(/[^a-z0-9]/g, '');
    const l = lastName
      .trim()
      .toLowerCase()
      .replace(/['’]/g, '')
      .replace(/[^a-z0-9]/g, '');
    if (f && l) {
      return `${f}.${l}@${PAYROLL_CONFIG.rosterEmailDomain}`;
    }
  }
  const normalized = externalStudentId
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9._-]/g, '');
  return `${normalized}@${PAYROLL_CONFIG.rosterEmailDomain}`;
}

function generateTempPassword(length = 12): string {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789!@#$';
  let out = '';
  const cryptoObj = typeof crypto !== 'undefined' ? crypto : null;
  if (cryptoObj?.getRandomValues) {
    const arr = new Uint32Array(length);
    cryptoObj.getRandomValues(arr);
    for (let i = 0; i < length; i += 1) out += chars[arr[i] % chars.length];
    return out;
  }
  for (let i = 0; i < length; i += 1) out += chars[Math.floor(Math.random() * chars.length)];
  return out;
}

export { buildRosterEmail, generateTempPassword };
